import makeStyles from '@mui/styles/makeStyles';
import classNames from "classnames";
import {Check} from "lucide-react";
import {isEmpty, noop} from 'lodash';
import React from "react";
import {useTranslation} from "react-i18next";
import {isMentor} from "business/role";
import {color, radius, type} from "common/tokens";
import {MenteeArt, MentorArt} from "components/redesign/RoleArt";

// One card above the other, each laid out as artwork beside its text and in
// the colour that role already carries elsewhere in the app: coral is the
// mentor on the profile cards, so the mentee takes the tertiary blue. Both
// cards stay tinted whether or not they are picked — the choice reads from the
// border and the tick, which is why the old pair of greyed-out illustrations
// is no longer needed.
const useStyles = makeStyles({
  options: {
    display: 'grid',
    gap: 16,
  },
  option: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: 24,
    padding: '20px 56px 20px 24px',
    borderRadius: radius.CARD,
    border: '2px solid transparent',
    cursor: 'pointer',
    transition: 'transform 150ms ease, box-shadow 150ms ease',
    '&:hover': {
      transform: 'translateY(-2px)',
    },
  },
  copy: {
    minWidth: 0,
  },
  mentee: {
    background: color.TERTIARY_TINT,
  },
  mentor: {
    background: color.PARTNER_TINT,
  },
  menteeSelected: {
    borderColor: color.TERTIARY,
  },
  mentorSelected: {
    borderColor: color.PARTNER,
  },
  // The radio carries the semantics; the card is what you see.
  input: {
    position: 'absolute',
    opacity: 0,
    width: 0,
    height: 0,
  },
  art: {
    width: 108,
    height: 94,
    flexShrink: 0,
    display: 'block',
  },
  label: {
    ...type.eyebrow,
    color: color.TEXT_SUBTLE,
    margin: 0,
  },
  role: {
    ...type.pageTitle,
    color: color.TEXT,
    margin: '2px 0 0',
  },
  description: {
    ...type.body,
    color: color.TEXT_MUTED,
    margin: '10px 0 0',
  },
  tick: {
    position: 'absolute',
    top: 12,
    right: 12,
    width: 24,
    height: 24,
    borderRadius: radius.PILL,
    color: color.SURFACE,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  menteeTick: {
    background: color.TERTIARY,
  },
  mentorTick: {
    background: color.PARTNER,
  },
});

const OPTIONS = [
  {key: 'Mentee', labelKey: 'mentee', descriptionKey: 'menteeRoleDescription', Art: MenteeArt, tone: 'mentee'},
  {key: 'Mentor', labelKey: 'mentor', descriptionKey: 'mentorRoleDescription', Art: MentorArt, tone: 'mentor'},
];

const MentorMenteeSelector = ({onAnswered = noop, userType = ""}) => {
  const classes = useStyles();
  const {t} = useTranslation();

  return (
    <div className={classes.options} role={'radiogroup'} aria-label={t('signUpMenteeMentorPageTitle')}>
      {OPTIONS.map(({key, labelKey, descriptionKey, Art, tone}) => {
        const selected = !isEmpty(userType) && isMentor(userType) === isMentor(key);

        return (
          <label
            key={key}
            className={classNames(
              classes.option,
              classes[tone],
              selected && classes[`${tone}Selected`]
            )}
          >
            <input
              type={'radio'}
              name={'userType'}
              value={key}
              checked={selected}
              className={classes.input}
              onChange={event => onAnswered({userType: event.target.value})}
            />
            <Art className={classes.art}/>
            <div className={classes.copy}>
              <p className={classes.label}>{t('iAmA')}</p>
              <p className={classes.role}>{t(labelKey)}</p>
              <p className={classes.description}>{t(descriptionKey)}</p>
            </div>
            {selected && (
              <span className={classNames(classes.tick, classes[`${tone}Tick`])}>
                <Check size={14} strokeWidth={3}/>
              </span>
            )}
          </label>
        );
      })}
    </div>
  );
};

export default MentorMenteeSelector;
